import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Coal | How revenue works",
  "description": "The Department of the Interior is responsible for 570 million acres of federal land with coal resources. This responsibility comes from the Mineral Leasing Act of 1920 and the Mineral Leasing Act for Acquired Lands of 1947, as amended.",
  "includeToc": false,
  "tags": ["How revenue works", "Coal", "Process", "Resources"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Box = makeShortcode("Box");
const Container = makeShortcode("Container");
const Grid = makeShortcode("Grid");
const BlmLogoImg = makeShortcode("BlmLogoImg");
const OnrrLogoImg = makeShortcode("OnrrLogoImg");
const CoalBackground = makeShortcode("CoalBackground");
const DidYouKnowCard = makeShortcode("DidYouKnowCard");
const CollapsibleContent = makeShortcode("CollapsibleContent");
const ProcessCard = makeShortcode("ProcessCard");
const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <Box bgcolor='primary.main' mdxType="Box">
      <Container maxWidth="lg" mdxType="Container">
        <Grid container alignItems='center' mdxType="Grid">
          <Grid item xs={12} sm={8} mdxType="Grid">
            <Box p={3} pl={0} mdxType="Box">
              <p><a parentName="p" {...{
                  "href": "/sites/About-Page-Updates/how-revenue-works/"
                }}>{`How revenue works /`}</a></p>
              <h1 {...{
                "id": "Coal",
                "style": {
                  "position": "relative"
                }
              }}>{`Coal`}</h1>
              <p>{`The Department of the Interior is responsible for 570 million acres of federal land with coal resources. This responsibility comes from the Mineral Leasing Act of 1920 and the Mineral Leasing Act for Acquired Lands of 1947, as amended.`}</p>
            </Box>
          </Grid>
          <Grid container xs={12} sm={4} mdxType="Grid">
            <Box p={3} border={4} borderTop={0} borderBottom={0} borderColor='white' mdxType="Box">
              <Grid container xs={12} mdxType="Grid">
                <Grid item xs={8} mdxType="Grid">
                  <p>{`Coal is managed and regulated by the `}<a parentName="p" {...{
                      "href": "https://www.blm.gov/"
                    }}>{`Bureau of Land Management`}</a>{` (BLM).`}</p>
                </Grid>
                <Grid item xs={4} mdxType="Grid">
                  <Box p={4} mdxType="Box"><BlmLogoImg mdxType="BlmLogoImg"></BlmLogoImg></Box>
                </Grid>
                <Grid item xs={8} mdxType="Grid">
                  <p>{`The `}<a parentName="p" {...{
                      "href": "http://www.onrr.gov/"
                    }}>{`Office of Natural Resources Revenue`}</a>{` (ONRR) manages some monetary transactions.`}</p>
                </Grid>
                <Grid item xs={4} mdxType="Grid">
                  <Box p={4} mdxType="Box"><OnrrLogoImg mdxType="OnrrLogoImg"></OnrrLogoImg></Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
    <CoalBackground mdxType="CoalBackground">
      <Grid container spacing={2} alignItems='flex-start' mdxType="Grid">
        <Grid item xs={12} sm={4} mdxType="Grid">
          <Box p={9} m={9} display={{
            xs: 'none',
            sm: 'block'
          }} mdxType="Box" />
          <Box p={8} display={{
            xs: 'block',
            sm: 'none'
          }} mdxType="Box" />
          <DidYouKnowCard mdxType="DidYouKnowCard">
            <p>{`“Fair market value” is determined differently by different state offices.`}</p>
            <CollapsibleContent mdxType="CollapsibleContent">
              <p>{`BLM guidance allows for flexibility in how state offices estimate fair market value, according to the Government Accountability Office (GAO). Some offices only consider recent comparable sites, while others also estimate future revenue. The GAO report also found that some state offices were falling short on documenting and reviewing fair market value determinations.`}</p>
            </CollapsibleContent>
          </DidYouKnowCard>
        </Grid>
        <Grid item xs={12} sm={1} mdxType="Grid">
        </Grid>
        <Grid item xs={12} sm={7} mdxType="Grid">
          <Box mt={5} mdxType="Box">
            <ProcessCard step={1} name={'Plan'} mdxType="ProcessCard">
              <p>{`BLM evaluates federal lands for potential coal extraction through the multiple-use planning process. This process includes:`}</p>
              <ul>
                <li parentName="ul">{`Identifying coal to potentially extract on federal land.`}</li>
                <li parentName="ul">{`Determining whether the land is suitable for coal extraction.`}</li>
                <li parentName="ul">{`Considering multiple-use conflicts stemming from other desirable uses for the land.`}</li>
                <li parentName="ul">{`Consulting with the surface owner when the government owns the coal below the ground and another party owns the land on the surface.`}</li>
              </ul>
              <p>{`If developing coal resources could conflict with protections or managing other resources and land uses, BLM may add stipulations or restrictions to a lease.`}</p>
            </ProcessCard>
            <ProcessCard step={2} name={'Lease'} mdxType="ProcessCard">
              <p>{`The Federal Coal Leasing Amendments Act of 1976 requires the federal government to lease coal competitively, meaning that any interested party can bid on a coal lease. BLM primarily uses the lease by application process. During this process, the public nominates a coal tract for BLM to sell. BLM reviews each application submitted by the public to make sure it complies with land-use plans. Next, a Regional Coal Team consisting of members from federal, state, local, and tribal governments reviews the application, consults the public, and decides whether to continue, change, or reject the application. At this point, BLM prepares an `}<GlossaryTerm mdxType="GlossaryTerm">{`Environmental Impact Statement (EIS)`}</GlossaryTerm>{` or Environmental Assessment for public comment in accordance with the National Environmental Policy Act.`}</p>
              <br />
              <p>{`Next, BLM prepares to sell the lease. In advance of the sale, BLM estimates the `}<GlossaryTerm mdxType="GlossaryTerm">{`fair market value`}</GlossaryTerm>{` of the coal lease. BLM holds a lease sale where each bidder submits a sealed bid, and BLM opens the bids publicly. The highest bid wins, so long as it is greater than or equal to the coal tract’s presale estimated fair market value, and the bidder meets all requirements (such as paying fees). Once BLM accepts a bid, the bidder must pay one fifth of the `}<GlossaryTerm mdxType="GlossaryTerm">{`bonus`}</GlossaryTerm>{` and the first year’s rent.`}</p>
            </ProcessCard>
            <ProcessCard step={3} name={'Explore'} mdxType="ProcessCard">
              <p>{`The lease holder must obtain a Coal Exploration License from the BLM to explore the lands for coal deposits. Exploration is the process of discovering the specific location, quantity, and quality of natural resources on leased land. Exploration typically takes place after leasing. In some instances, exploration by multiple parties occurs before leasing to increase competition during the lease sale.`}</p>
              <br />
              <p>{`To apply for a Coal Exploration License, companies must submit an Exploration Plan. The Exploration Plan details the timing, location, method, and potential environmental impact of all exploration activities. The Coal Exploration License expires after two years. During the explore phase, companies pay `}<GlossaryTerm mdxType="GlossaryTerm">{`rent`}</GlossaryTerm>{` and bonus installments to ONRR.`}</p>
            </ProcessCard>
            <ProcessCard step={4} name={'Develop'} mdxType="ProcessCard">
              <p>{`After BLM awards the lease, the lease holder must obtain the appropriate permits and licenses from:`}</p>
              <ul>
                <li parentName="ul">{`the BLM,`}</li>
                <li parentName="ul">{`the Office of Surface Mining Reclamation and Enforcement (OSMRE), and `}</li>
                <li parentName="ul">{`any affected state and local governments before they can begin developing the site and producing coal. `}</li>
              </ul>
              <p>{`These permits and licenses must be obtained before they can begin developing the site and producing coal. As soon as coal production achieves `}<GlossaryTerm mdxType="GlossaryTerm">{`paying quantities`}</GlossaryTerm>{`, the lease holder stops paying rent and starts paying royalties to ONRR. The lease holder also pays `}<GlossaryTerm mdxType="GlossaryTerm">{`Abandoned Mine Land Fees`}</GlossaryTerm>{` to OSMRE for coal produced.`}</p>
            </ProcessCard>
            <ProcessCard step={5} name={'Decommission and reclaim'} mdxType="ProcessCard">
              <p>{`At the close of a coal mining operation, the lease holder must decommission the mine and restore the land. State governments, with oversight from OSMRE, regulate and oversee this process. Even before gaining a lease, the lease holder must submit a bond to OSMRE or a state regulatory agency as insurance for complying with the lease and covering the cost of `}<GlossaryTerm mdxType="GlossaryTerm">{`reclaiming`}</GlossaryTerm>{` the land.`}</p>
              <br />
              <p>{`Within coal mining, federal and local governments partner to regulate reclamation. OSMRE is responsible for establishing a nationwide program to protect society and the environment from the adverse effects of surface coal mining operations. To do so, OSMRE works with states and tribes to ensure that citizens and the environment are protected during coal mining. OSMRE ensures that the land is restored to beneficial use when mining is finished. OSMRE and its partners are also responsible for reclaiming and restoring lands and waters degraded by mining operations before 1977.`}</p>
            </ProcessCard>
            <ProcessCard name={'Rates and fees'} defaultExpanded={true} mdxType="ProcessCard">
              <ul>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "/sites/About-Page-Updates/how-revenue-works/revenues/#Coal"
                  }}>{`Coal rates and fees`}</a></li>
              </ul>
            </ProcessCard>
            <ProcessCard name={'Learn more'} defaultExpanded={true} mdxType="ProcessCard">
              <ul>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "https://www.blm.gov/programs/energy-and-minerals/coal"
                  }}>{`Coal leasing`}</a></li>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "https://www.blm.gov/programs/energy-and-minerals/coal/land-use-planning"
                  }}>{`Land use planning`}</a></li>
              </ul>
            </ProcessCard>
          </Box>
        </Grid>
      </Grid>
    </CoalBackground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      